import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useCatalogDialog } from "@/hooks/use-catalog-dialog";
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { useTranslation } from "react-i18next";
import { DirtyIndicator } from "@/components/ui/dirty-indicator";
import {
  usePagination,
  usePageOffset,
  DEFAULT_PAGE_SIZE,
} from "@/hooks/use-pagination";
import { useNavigate } from "@tanstack/react-router";
import { getCurrentUser } from "@/lib/api/auth";

import { formatAdminDate } from "@/components/admin/formatting";
import { DeletedIndicatorCell } from "@/components/admin/catalogs/deleted-indicator-cell";
import { DeletedWarningBanner } from "@/components/admin/catalogs/deleted-warning-banner";
import { fromIntlLanguage } from "@/lib/i18n/config";
import { AdaptiveSheet } from "@/components/ui/adaptive-sheet";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ErrorState, Text } from "@/components/ui/text";
import { Fab } from "@/components/ui/fab";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { FormLabel } from "@/components/ui/form-label";
import { Icon } from "@/components/ui/icons";
import { InputWrapper } from "@/components/ui/input-wrapper";
import { PageLoadingOverlay } from "@/components/ui/page-loading-overlay";
import { ModalBody, ModalFooter, ModalHeader } from "@/components/ui/modal";
import { PageTitle } from "@/contexts/page-title-context";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { ResizeHandle } from "@/components/ui/resize-handle";
import {
  ResizableTableShell,
  type ColumnDef,
} from "@/components/ui/resizable-table-shell";
import { HStack } from "@/components/ui/h-stack";
import { StickyHeaderSlot } from "@/components/ui/sticky-header-slot";
import { TableViewport } from "@/components/ui/table-viewport";
import { VStack } from "@/components/ui/v-stack";
import { useSizeClass } from "@/hooks/use-size-class";
import { TableSkeleton } from "@/components/admin/table-skeleton";
import type { Ref } from "@/lib/api/types";
import { getTruckManufacturers } from "@/lib/api/trucks";
import {
  createTruckManufacturer,
  updateTruckManufacturer,
} from "@/lib/api/catalogs";
import { ConfirmActionButton } from "@/components/admin/catalogs/confirm-action-button";
import { useDeleteRestore } from "@/hooks/useDeleteRestore";
import { useForm } from "@/hooks/useForm";
import { z } from "zod";
import { useGuardedClose } from "@/hooks/useGuardedClose";

type ManufacturerRow = Ref & { created_at?: string; is_deleted?: boolean };

function AdminTruckManufacturersCatalogPage() {
  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";
  const navigate = useNavigate();
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tCatalogTruckManufacturers } = useTranslation(
    "admin.catalogs.truckManufacturers",
  );
  const { t: tCommon } = useTranslation("common");

  const queryClient = useQueryClient();
  const { offset } = usePageOffset();
  const {
    data,
    isLoading: loading,
    isError: loadError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.admin.truckMakes({ limit: DEFAULT_PAGE_SIZE, offset }),
    queryFn: () => getTruckManufacturers({ limit: DEFAULT_PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });
  const items = useMemo(
    () => (data?.data ?? []) as unknown as ManufacturerRow[],
    [data?.data],
  );
  const total = data?.total ?? 0;
  const { page, totalPages, setPage } = usePagination({ total });
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [query, setQuery] = useState("");

  const {
    isOpen: modalOpen,
    selectedItem: editing,
    openCreate: catalogOpenCreate,
    openEdit: catalogOpenEdit,
    close: catalogClose,
  } = useCatalogDialog<ManufacturerRow>();
  const form = useForm({
    schema: z.object({ name: z.string().min(1) }),
    defaults: { name: "" },
    onSubmit: async () => {},
  });
  const formData = form.fields;
  const isDirty = form.isDirty;
  const setFormData = (updater: (prev: typeof formData) => typeof formData) => {
    form.setFields(updater(form.fields));
  };
  const { handleClose, confirmDialog: closeConfirmDialog } = useGuardedClose(
    isDirty,
    catalogClose,
  );

  const { loading: deleteLoading, toggle: toggleDelete } =
    useDeleteRestore<ManufacturerRow>({
      endpoint: "/truck-manufacturers",
      onSuccess: (_isDeleted, data) => {
        catalogOpenEdit(data as ManufacturerRow);
        void queryClient.invalidateQueries({
          queryKey: ["admin", "catalogs", "truck-makes"],
        });
      },
    });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.name.toLowerCase().includes(q));
  }, [items, query]);

  function openCreate() {
    catalogOpenCreate();
    form.reset({ name: "" });
  }

  function openEdit(item: ManufacturerRow) {
    catalogOpenEdit(item);
    form.reset({ name: item.name });
  }

  const isValid = useMemo(() => Boolean(formData.name.trim()), [formData]);

  async function handleSave() {
    try {
      setSubmitting(true);
      setSaveError(false);

      if (editing) {
        await updateTruckManufacturer(editing.id, { name: formData.name });
      } else {
        await createTruckManufacturer({ name: formData.name });
      }

      void queryClient.invalidateQueries({
        queryKey: ["admin", "catalogs", "truck-makes"],
      });
      form.reset();
      catalogClose();
    } catch (error) {
      console.error("Failed to save truck manufacturer", error);
      setSaveError(true);
    } finally {
      setSubmitting(false);
    }
  }

  // ─── Resizable columns ──────────────────────────────────────────────
  const userId = getCurrentUser()?.id ?? null;
  const columnDefs: ColumnDef[] = [
    { key: "deleted", defaultWidth: 40, minWidth: 40 },
    { key: "name", defaultWidth: 200, minWidth: 100 },
    { key: "modelsCount", defaultWidth: 120, minWidth: 60 },
    { key: "created", defaultWidth: 140, minWidth: 80 },
    { key: "actions", defaultWidth: 200, minWidth: 120 },
  ];

  return (
    <VStack gap="lg" data-testid="admin-catalog-truck-manufacturers-page">
      <VStack gap="none">
        <PageTitle>{tCatalogTruckManufacturers("title")}</PageTitle>
      </VStack>
      <StickyHeaderSlot>
        <Card variant="bordered" padding="xs">
          <FilterToolbar
            searchPlaceholder={tCatalogTruckManufacturers("searchPlaceholder")}
            searchValue={query}
            onSearchChange={setQuery}
            createAction={{
              label: tCatalogTruckManufacturers("add"),
              onClick: openCreate,
            }}
            pagination={{
              page,
              totalPages,
              perPage: DEFAULT_PAGE_SIZE,
              onPageChange: setPage,
              loading,
            }}
            data-testid="catalog-truck-manufacturers-toolbar"
          />
        </Card>
      </StickyHeaderSlot>

      <Card variant="bordered" padding="none">
        <TableViewport position="relative">
          {loadError || saveError ? (
            <ErrorState
              title={
                loadError
                  ? tCatalogTruckManufacturers("failedToLoad")
                  : tCatalogTruckManufacturers("failedToSave")
              }
              action={
                loadError ? (
                  <Button
                    color="neutral"
                    variant="outline"
                    onClick={() => void refetch()}
                  >
                    {tCommon("retry")}
                  </Button>
                ) : undefined
              }
              className="py-10"
            />
          ) : null}

          {loading && items.length === 0 ? (
            <TableSkeleton
              headers={[
                "",
                tCatalogTruckManufacturers("columns.name"),
                tCatalogTruckManufacturers("columns.modelsCount"),
                tCatalogTruckManufacturers("columns.created"),
                tCatalogTruckManufacturers("columns.actions"),
              ]}
            />
          ) : filtered.length === 0 ? (
            <Text color="muted">{tCatalogTruckManufacturers("noItems")}</Text>
          ) : (
            <ResizableTableShell
              tableId="catalog-truck-manufacturers"
              userId={userId}
              columns={columnDefs}
            >
              {({ onResizeStart }) => (
                <>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell className="w-10" />
                      <TableHeaderCell className="relative">
                        {tCatalogTruckManufacturers("columns.name")}
                        <ResizeHandle
                          columnKey="name"
                          onResizeStart={onResizeStart}
                        />
                      </TableHeaderCell>
                      <TableHeaderCell className="relative">
                        {tCatalogTruckManufacturers("columns.modelsCount")}
                        <ResizeHandle
                          columnKey="modelsCount"
                          onResizeStart={onResizeStart}
                        />
                      </TableHeaderCell>
                      <TableHeaderCell className="relative">
                        {tCatalogTruckManufacturers("columns.created")}
                        <ResizeHandle
                          columnKey="created"
                          onResizeStart={onResizeStart}
                        />
                      </TableHeaderCell>
                      <TableHeaderCell className="relative text-right" sticky>
                        {tCatalogTruckManufacturers("columns.actions")}
                        <ResizeHandle
                          columnKey="actions"
                          onResizeStart={onResizeStart}
                        />
                      </TableHeaderCell>
                      <TableHeaderCell className="w-auto" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((it) => {
                      const isDeleted = Boolean(it.is_deleted);
                      const mutedClass = isDeleted
                        ? "text-[color:var(--on-surface-variant)] opacity-70"
                        : "";
                      return (
                        <TableRow key={it.id}>
                          <TableCell className="w-10">
                            <DeletedIndicatorCell isDeleted={isDeleted} />
                          </TableCell>
                          <TableCell className={`font-medium ${mutedClass}`}>
                            {it.name}
                          </TableCell>
                          <TableCell className={mutedClass}>—</TableCell>
                          <TableCell className={mutedClass}>
                            {formatAdminDate(it.created_at, language)}
                          </TableCell>
                          <TableCell className="text-right" sticky>
                            <HStack justify="end">
                              <Button
                                type="button"
                                color="neutral"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  void navigate({
                                    to: `/admin/catalogs/truck-models?manufacturer_id=${it.id}`,
                                  })
                                }
                              >
                                {tCatalogTruckManufacturers("viewModels")}
                              </Button>
                              <Button
                                type="button"
                                color="neutral"
                                variant="outline"
                                size="sm"
                                onClick={() => openEdit(it)}
                              >
                                {tCatalogTruckManufacturers("edit")}
                              </Button>
                            </HStack>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </>
              )}
            </ResizableTableShell>
          )}
          {loading && items.length > 0 && (
            <PageLoadingOverlay
              page={page}
              label={tCommon("pagination.loadingPage")}
            />
          )}
        </TableViewport>
      </Card>

      {isCompact && (
        <Fab
          onClick={openCreate}
          aria-label={tCatalogTruckManufacturers("add")}
          data-testid="catalog-truck-manufacturers-fab"
        />
      )}

      <AdaptiveSheet
        open={modalOpen}
        onDismiss={handleClose}
        onSubmit={() => void handleSave()}
        size="md"
        data-testid="admin-truck-manufacturer-modal"
      >
        <ModalHeader>
          <Text weight="medium">
            {editing
              ? tCatalogTruckManufacturers("modalEditTitle")
              : tCatalogTruckManufacturers("modalAddTitle")}
            {isDirty ? <DirtyIndicator /> : null}
          </Text>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={submitting}
          >
            <Icon.X size="sm" />
          </Button>
        </ModalHeader>
        <ModalBody className="space-y-3">
          {editing && editing.is_deleted ? <DeletedWarningBanner /> : null}
          <VStack gap="xs">
            <FormLabel>{tCatalogTruckManufacturers("fields.name")}</FormLabel>
            <InputWrapper
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              disabled={submitting}
              data-testid="admin-truck-manufacturer-name"
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack align="stretch" gap="lg">
            {editing ? (
              <ConfirmActionButton
                isRestore={Boolean(editing.is_deleted)}
                loading={deleteLoading}
                disabled={submitting}
                onConfirm={() =>
                  void toggleDelete(editing.id, Boolean(editing.is_deleted))
                }
                className="mr-3"
              />
            ) : null}
            <Button
              type="button"
              color="neutral"
              variant="outline"
              onClick={handleClose}
              disabled={submitting || deleteLoading}
            >
              {tCommon("cancel")}
            </Button>
            <Button
              type="button"
              loading={submitting}
              disabled={
                submitting ||
                deleteLoading ||
                !isValid ||
                (Boolean(editing) && !isDirty)
              }
              onClick={() => void handleSave()}
            >
              {tCommon("save")}
            </Button>
          </HStack>
        </ModalFooter>
      </AdaptiveSheet>
      {closeConfirmDialog}
    </VStack>
  );
}

export const Route = createFileRoute(
  "/_app/admin/catalogs/truck-manufacturers",
)({
  component: AdminTruckManufacturersCatalogPage,
});
